<script>
  import { computed, ref, onMounted, onBeforeUnmount } from '@nuxtjs/composition-api'

  import ProductCard from '@/components/Ecommerce/Products/ProductCard.vue'
  import CarouselBase from '@/components/CarouselBase'
  import CarouselControls from '@/components/CarouselControls'
  import ProductCardSkeleton from '@/components/Ecommerce/Products/ProductCardSkeleton.vue'

  import { useCarousel } from '@/composables/useCarousel'

  export default {
    components: {
      ProductCard,
      CarouselBase,
      CarouselControls,
      ProductCardSkeleton
    },
    props: {
      products: {
        type: Array,
        default: () => []
      }
    },
    setup (props) {
      const {
        hasNext,
        hasPrev,
        next,
        prev,
        slide
      } = useCarousel()

      const isDesktop = ref(false)

      const checkScreenSize = () => {
        isDesktop.value = window.innerWidth >= 768 // md breakpoint
      }

      onMounted(() => {
        checkScreenSize()
        window.addEventListener('resize', checkScreenSize)
      })

      onBeforeUnmount(() => {
        window.removeEventListener('resize', checkScreenSize)
      })

      const hasProducts = computed(() => props.products.length)

      const normalizedProducts = computed(() => {
        if (!hasProducts.value) {
          return Array.from(Array(12).keys())
        }

        // Handle both formats: direct products and products with .item wrapper
        return props.products.map(product => {
          return product.item || product
        })
      })

      const sliderIsNotNeededOnDesktop = computed(() => {
        return normalizedProducts.value.length <= 5
      })

      const shouldDisableDrag = computed(() => {
        return sliderIsNotNeededOnDesktop.value && isDesktop.value
      })

      return {
        normalizedProducts,
        sliderIsNotNeededOnDesktop,
        shouldDisableDrag,
        hasNext,
        slide,
        next,
        prev,
        hasPrev
      }
    }
  }
</script>

<template>
  <div
    v-if="products"
    class="max-w-full"
  >
    <div class="relative w-full product-slider-wrapper">
      <CarouselControls
        class="absolute left-0 right-0 z-30 justify-between hidden px-4 transform pointer-events-none md:flex top-center translate-y-center"
        :class="{'md:hidden': sliderIsNotNeededOnDesktop}"
        :has-prev="slide > 0"
        :has-next="hasNext"
        @next="next()"
        @prev="prev()"
      />

      <CarouselBase
        class="gap-2 pt-1 pb-1 mr-4 related-product-slider md:px-0"
        :active-slide="slide"
        :items="normalizedProducts"
        show-progress
        :space-between="8"
        small-slider
        :disable-drag="shouldDisableDrag"
        @slide-change="slide = $event"
        @has-next="hasNext = $event"
      >
        <!-- Keyed with the index too: a repeated productId produced duplicate
             keys, which left the slider and its dots patching the wrong nodes.
             The duplicates themselves are filtered in getCrossupsellProducts. -->
        <div
          v-for="(product, index) in normalizedProducts"
          :key="`${product.productId || 'item'}-${index}`"
          class="slide"
        >
          <transition
            name="fade"
            mode="out-in"
          >
            <ProductCard
              v-if="isNaN(product)"
              :product="product"
            />
            <ProductCardSkeleton v-else />
          </transition>
        </div>
      </CarouselBase>
      <div
        class="px-4"
        :class="{'md:hidden': sliderIsNotNeededOnDesktop}"
      >
        <ul class="flex flex-row justify-center gap-2 my-4 align-center carousel-dots">
          <li
            v-for="(product, index) in normalizedProducts"
            :key="`${product.productId || 'item'}-${index}`"
            tabindex="0"
            class="w-2 h-2 bg-gray-500 rounded-full"
            :class="{'bg-blue-500': slide === index}"
            @click="slide = index"
            @keydown.enter="slide = index"
            @keydown.space.prevent="slide = index"
          >
            <span class="hidden">
              {{ index }}
            </span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.related-product-slider .slide {
    width: 175px !important;

      @screen md {
    width: 220px !important;
  }
}

.product-slider-wrapper {
  .carousel-controls {
    button {
      @apply pointer-events-auto;
      @apply bg-white;

      &:first-child {
        transform: translateX(-2rem);
      }
      &:last-child {
        transform: translateX(2rem);
      }
    }
  }
}
</style>
